import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import i18n from 'live/services/i18n'
import { cloneDeep, capitalize } from 'lodash'
import { AddRowButton, RowRemoveButton } from '../common/components'
import ActionToggle from 'live/components/ui/action-toggle'
import { HotTable, HotColumn } from '@handsontable/react'

interface IProps {
    deviations: Deviations
    setDeviations: (deviations: Deviations) => void
    rating: 'packaging' | 'appearance' | 'texture' | 'taste'
    activeAnalysis: ActiveAnalysis
    setActiveAnalysis: (activeAnalysis: ActiveAnalysis) => void
}

export function DeviationsTable({
    deviations,
    setDeviations,
    rating,
    activeAnalysis,
    setActiveAnalysis
}: IProps): JSX.Element {
    const [showDeviations, setShowDeviations] = useState<boolean>(false)

    const rowHeight = 25

    useEffect(() => {
        setShowDeviations(activeAnalysis[rating])
    }, [activeAnalysis[rating]])

    const onChangeDeviation = (index: number, value: string): void => {
        if (activeAnalysis[rating]) {
            const deviationsData = cloneDeep(deviations)
            deviationsData[rating][index] = value
            setDeviations(deviationsData)
        }
    }

    const onChangeTable = (changes): any => {
        if (activeAnalysis[rating] && changes) {
            const deviationsData = cloneDeep(deviations)
            changes.map(change => {
                if (change[3] === null) {
                    const elementToBeRemovedIndex = deviationsData[rating].indexOf(change[2])
                    deviationsData[rating].splice(elementToBeRemovedIndex, 1)
                } else if (change[0] > deviationsData[rating].length) {
                    deviationsData[rating].push(change[3])
                } else {
                    deviationsData[rating][change[0]] = change[3]
                }
            })
            setDeviations(deviationsData)
        }
    }

    const addRow = (): void => {
        const deviationsData = cloneDeep(deviations)
        deviationsData[rating].push('')
        setDeviations(deviationsData)
    }

    const removeRow = (index, event): void => {
        event.preventDefault()
        const updatedInfo = cloneDeep(deviations)
        updatedInfo[rating].splice(index, 1)
        setDeviations(updatedInfo)
    }

    const formatToTableDataArray = (deviations: string[]): string[][] => {
        const tableData = []
        deviations.forEach(deviation => {
            tableData.push([deviation])
        })

        return tableData
    }

    return (
        <StyledDeviationTable>
            <div className={`title${showDeviations ? '' : 'Closed'}`}>
                {' '}
                <ActionToggle
                    className={'centered'}
                    active={activeAnalysis[rating]}
                    onChange={() => {
                        setShowDeviations(!activeAnalysis[rating])
                        setActiveAnalysis({ ...activeAnalysis, [rating]: !activeAnalysis[rating] })
                    }}
                />{' '}
                {capitalize(i18n(rating))}
                <div
                    className={`title--chevron${activeAnalysis[rating] ? '' : '__off'}`}
                    onClick={() => {
                        if (activeAnalysis[rating]) {
                            setShowDeviations(!showDeviations)
                        }
                    }}
                >
                    <i className={`fas fa-chevron-${showDeviations ? 'up' : 'down'}`} />
                </div>
            </div>

            {showDeviations ? (
                <div>
                    <HotTable
                        className="hotTable--insert"
                        afterChange={change => onChangeTable(change)}
                        data={formatToTableDataArray(deviations[rating])}
                        stretchH={'all'}
                        rowHeights={rowHeight}
                        height={deviations[rating].length * rowHeight}
                    >
                        <HotColumn></HotColumn>
                    </HotTable>

                    {activeAnalysis[rating] ? (
                        <AddRowButton onClick={addRow} className="deviations--insert">
                            <i className="fas fa-plus" /> {`${i18n('add')} ${i18n('Deviation')}`}
                        </AddRowButton>
                    ) : null}
                </div>
            ) : null}
        </StyledDeviationTable>
    )
}
const StyledDeviationTable = styled.div`
    --border: 1px solid ${({ theme }) => theme['gray-400']};
    width: 100%;
    border-radius: 3px;
    display: flex;
    flex-direction: column;
    align-self: start;
    margin-bottom: 16px;
    .title {
        background: #eeeeee;
        width: 100%;
        border: 1px solid #c8ccd4;
        font-family: ${({ theme }) => theme['font-semibold']};
        display: flex;
        align-items: center;
        font-size: 14px;
        height: 32px;
        border-top-left-radius: 3px;
        border-top-right-radius: 3px;
        padding: 12px 24px;
        &--chevron {
            margin-left: auto;
            margin-right: 0;
            color: #37a3ef;
            &__off {
                color: ${({ theme }) => theme['blue-400']};
                margin-left: auto;
                margin-right: 0;
            }
        }
        &--closed {
            border-bottom-left-radius: 3px;
            border-bottom-right-radius: 3px;
        }
    }

    .titleClosed {
        background: #eeeeee;
        width: 100%;
        border: 1px solid #c8ccd4;
        font-family: ${({ theme }) => theme['font-semibold']};
        display: flex;
        align-items: center;
        font-size: 14px;
        border-radius: 3px;
        padding: 12px 24px;
        height: 32px;
        &--chevron {
            margin-left: auto;
            margin-right: 0;
            color: #37a3ef;
        }
    }

    .deviations {
        display: flex;
        align-items: center;
        display: flex;
        width: 100%;
        border: 1px solid #c8ccd4;
        height: 41px;
        border-top: none;
        font-size: 14px;
        &--input {
            width: 100%;
            border: 1px solid transparent;
            padding-left: 5px;
        }
        &--disabled {
            color: ${({ theme }) => theme['gray-400']};
            background: #f5f5f5;
            width: 100%;
            border: 1px solid transparent;
            padding-left: 5px;
            height: 40px;
            padding-top: 10px;
        }
        &--insert {
            border-top: none;
        }
        &--remove {
            border: 1px solid transparent;
        }
        &--hotTable {
            width: 100%;
        }
    }
    button {
        border: 1px solid #c8ccd4;
        border-bottom-left-radius: 3px;
        border-bottom-right-radius: 3px;
    }
`
